/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React from 'react';
import './Tour.css';
import searchImg from 'dndimages/search.png';
import Tour, { TourStep } from './Tour';

// A standalone tour for the Advanced Monster Search modal, launched from the '?' icon in that
// modal's top-right corner. The modal is already open while this runs, so (unlike the main tour) these
// steps don't open/close it or depend on the main tour's tourState — they only watch the search inputs.
interface TourGridSearchProps {
    currentStepIndex: number;
    searchMinCr: number | null;
    searchMaxCr: number | null;
    searchKeywords: string;
    tourState: string;
    searchSelectedEnvironments: string[];
    setCurrentStepIndex: (step: number) => void;
    onClose: (completed?: boolean) => void;
}

const TourGridSearch: React.FC<TourGridSearchProps> = ({ currentStepIndex, searchMinCr, searchMaxCr,
                                                        searchKeywords, tourState, searchSelectedEnvironments,
                                                        setCurrentStepIndex, onClose }) => {
    const kw = (...values: string[]) => values.includes(searchKeywords.toLowerCase().trim());

    const tourSteps: TourStep[] = [{
        highlightElement: '#monsterGridSearch',
        title: 'Advanced Monster Search',
        scrollIntoView: true,
        description: <div>This opens an advanced search dialog that lets you search for a monster by its CR level, environments and by keywords or phrases.
            This dialog uses all the monster data that is currently being loaded in the background.
            <div className='user-tour-action'>Click the <img src={searchImg} style={{ width: '32px', cursor: 'pointer' }} /> icon.</div>
            </div>,
        testToContinue: () => {return tourState === 'monsterGridOpen'},
        position: 'bottom'
    }, {
        highlightElement: '#AdvancedMonsterGrid',
        alternateElement: '#monsterGridSearch',
        positionNextToElement: '#MonsterGrid',
        title: 'Advanced Monster Search',
        scrollIntoView: true,
        description: <div>This advanced search lets you find monsters by their CR rating, environment, and by keywords or phrases.
            It searches across all the monsters from <a target="_blank" href="https://open5e.com/" rel="noreferrer">open5e.com</a>,
            as well as your personal monsters and any monsters shared by the GGG Community.
            <div className='user-tour-action'>Click <button className='fake-button'>Next</button>.</div>
        </div>,
        position: 'left'
    }, {
        highlightElement: '#AdvancedMonsterGrid',
        alternateElement: '#monsterGridSearch',
        positionNextToElement: '#MonsterGrid',
        title: 'Advanced Monster Search',
        scrollIntoView: true,
        description: <div>You can search for monsters based on their CR rating, enivronment and key words or phrases.
            Suppose we want to find monsters that have a breath weapon.
            <div className='user-tour-action'>Type 'breath weapon' (without the quotes) in the keywords.</div>
        </div>,
        testToContinue: () => kw('breath weapon'),
        position: 'left'
    }, {
        highlightElement: '#AdvancedMonsterGrid',
        alternateElement: '#monsterGridSearch',
        positionNextToElement: '#MonsterGrid',
        title: 'Advanced Monster Search',
        scrollIntoView: true,
        description: <div>The results show monsters that have both 'breath' and 'weapon', so we get lots of results, for monster that can hold their breath, and monsters that are amphibious ('can <b>breath</b>e air and water'), etc.
            To limit our results to only those with an actual breath weapon, surround it in quotes, forcing it to match the phrase "breath weapon".
            <div className='user-tour-action'>Put quotes (' or ") around the keywords 'breath weapon'.</div>
        </div>,
        testToContinue: () => kw('"breath weapon"', "'breath weapon'"),
        position: 'left'
    }, {
        highlightElement: '#AdvancedMonsterGrid',
        alternateElement: '#monsterGridSearch',
        positionNextToElement: '#MonsterGrid',
        title: 'Advanced Monster Search',
        scrollIntoView: true,
        description: <div>Now the results size is much more reasonable, a little over 100 monsters.
            The majority of these results are dragons. If we want to exclude dragons from these results, add the term "-dragon" (without the quotes).
            This will remove any monster that contains the word 'dragon' in its description anywhere.
            <div className='user-tour-action'>Add ' -dragon' to the keywords ('"breath weapon" -dragon').</div>
        </div>,
        testToContinue: () => kw('"breath weapon" -dragon', "'breath weapon' -dragon"),
        position: 'left'
    }, {
        highlightElement: '#AdvancedMonsterGrid',
        alternateElement: '#monsterGridSearch',
        positionNextToElement: '#MonsterGrid',
        title: 'Search Qualifiers',
        scrollIntoView: true,
        description: <div>Now the results shows only a few monsters that reference "breath weapon" but dont reference 'dragon' anywhere in their text.
            But this '-dragon' excluded more than just monsters with a 'type' of 'dragon'.
            If we want to exclude monsters of type 'dragon' from these results, we can add a qualifier to the "-dragon" term.
            Qualifiers are of the form "field:value". If we put "type:dragon" instead of "-dragon" we would see exclusively monsters with a 'type' of 'dragon'.
            If we put "type:-dragon", we would see exclusively monsters with a 'type' that excludes 'dragon'.
            <div className='user-tour-action'>Add 'type:' in front of '-dragon' in the keywords ('"breath weapon" type:-dragon').</div>
        </div>,
        testToContinue: () => kw('"breath weapon" type:-dragon', "'breath weapon' type:-dragon"),
        position: 'left'
    }, {
        highlightElement: '#AdvancedMonsterGrid',
        alternateElement: '#monsterGridSearch',
        positionNextToElement: '#MonsterGrid',
        title: 'Search Qualifiers',
        scrollIntoView: true,
        description: <div>Now the results size is 19 monsters, and we can see some monsters (like 'Dragon Cultist') we excluded from the simple "-dragon" search because of their name.
            Others (like 'Chimera'), were excluded because 'dragon' appeared in their description.
            <br/>You can use qualified searches to find things like "monsters that are immune to fire and are also evil" by adding the terms "immunities:fire align:evil"
            <div className='user-tour-action'>Clear the search keyword.</div>
        </div>,
        testToContinue: () => !searchKeywords,
        position: 'left'
    }, {
        highlightElement: '#AdvancedMonsterGrid',
        alternateElement: '#monsterGridSearch',
        positionNextToElement: '#MonsterGrid',
        title: 'Advanced Monster Search',
        scrollIntoView: true,
        description: <div>You can refine your search by the CR using the 'min CR' and 'max CR' controls in the 'CR Range' section.
            <div className='user-tour-action'>Use the CR Range section to find all monsters between CR 5 and CR 9.</div>
        </div>,
        testToContinue: () => searchMinCr === 5 && searchMaxCr === 9,
        position: 'left'
    }, {
        highlightElement: '#AdvancedMonsterGrid',
        alternateElement: '#monsterGridSearch',
        positionNextToElement: '#MonsterGrid',
        title: 'Advanced Monster Search',
        scrollIntoView: true,
        description: <div>You can also limit the results to show only monsters that list at least one of the environment you select from the 'Environments' control under the keywords control.
            <div className='user-tour-action'>Click on the red '--' under the keywords to open the environments control, and select 'Underworld'.</div>
        </div>,
        testToContinue: () => searchSelectedEnvironments.length === 1 && searchSelectedEnvironments[0] === 'Underworld',
        position: 'left'
    }, {
        highlightElement: '#AdvancedMonsterGrid',
        alternateElement: '#monsterGridSearch',
        positionNextToElement: '#MonsterGrid',
        title: 'Advanced Monster Search',
        scrollIntoView: true,
        description: <div>Close that environments control, and you'll see all the monsters with a CR of 5-9 that list 'Underworld' as one of their environments.
            <br/>It's worth noting that most sources other than 'WoTC-srd' do not list environments for their monsters.
            <div className='user-tour-action'>Click any monster's name in the results to load it into the editor.</div>
        </div>,
        testToContinue: () => {return tourState === 'monsterGridClosed'},
        position: 'left'
    }, {
        highlightElement: '#monsterGridSearch',
        title: 'Advanced Monster Search',
        scrollIntoView: true,
        description: <div>After you've loaded a monster from the search, when you return to the search your previous search parameters will still be selected.
            <br/><br/>That's the end of the Advanced Monster Search tour!
        </div>,
        position: 'left'
    }];

    if (currentStepIndex >= tourSteps.length) {
        return null;
    }
    return <Tour steps={tourSteps} currentStepIndex={currentStepIndex} setCurrentStepIndex={setCurrentStepIndex} onClose={onClose} tourId="search" />;
};

export default TourGridSearch;
